import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Copy,
  Download,
  Inbox,
  Lock,
  LogOut,
  Mail,
  MessageSquare,
  RefreshCw,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Container } from '@/components/layout/Container';
import { Logo } from '@/components/brand/Logo';
import { LanguageSwitcher } from '@/components/site/LanguageSwitcher';
import { WhatsAppGlyph } from '@/components/site/WhatsAppButton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  adminLeadDetail,
  adminLogin,
  adminStats,
  adminUpdateLead,
  clearAdminToken,
  downloadLeadsCsv,
  getAdminToken,
  listLeads,
  setAdminToken,
} from '@/lib/api';
import { AGENCY, whatsappLink } from '@/lib/site';
import { useI18n, interpolate } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const STATUSES = ['new', 'read', 'contacted', 'archived'];

/**
 * Leads are stored with canonical English values (service interest, budget
 * band, timeline band) so the CRM stays consistent no matter which language
 * the visitor used. Translate them back for display in the dashboard.
 */
const useCanonicalLabel = () => {
  const { t } = useI18n();
  return useMemo(() => {
    const map = {};
    [
      ...(t.serviceOptions || []),
      ...(t.budgetOptions || []),
      ...(t.timelineOptions || []),
    ].forEach((o) => {
      map[o.value] = o.label;
    });
    return (value) => (value && map[value]) || value;
  }, [t]);
};

/* ------------------------------------------------------------------ login */

const LoginGate = ({ onSuccess }) => {
  const { t } = useI18n();
  const copy = t.admin.login;
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setError(copy.empty);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { token } = await adminLogin(password);
      setAdminToken(token);
      onSuccess();
    } catch (err) {
      setError(err?.response?.status === 401 ? copy.wrong : copy.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center px-5 py-16"
      style={{ backgroundColor: 'var(--rmh-canvas)' }}
      data-testid="leads-login-gate"
    >
      <span
        aria-hidden="true"
        className="rmh-champagne-wash pointer-events-none fixed inset-0"
      />
      <div className="relative z-10 w-full max-w-[420px]">
        <div className="flex justify-center">
          <Logo variant="stacked" asLink={false} />
        </div>

        <form
          onSubmit={submit}
          noValidate
          data-testid="leads-login-form"
          className="mt-10 rounded-[18px] border p-7 sm:p-9"
          style={{
            backgroundColor: 'var(--rmh-surface)',
            borderColor: 'var(--rmh-border)',
            boxShadow: 'var(--shadow-soft)',
          }}
        >
          <span
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border"
            style={{
              backgroundColor: 'var(--rmh-surface-inset)',
              borderColor: 'var(--rmh-border)',
            }}
          >
            <Lock
              className="h-4 w-4"
              strokeWidth={1.6}
              style={{ color: 'var(--rmh-mocha)' }}
            />
          </span>
          <p className="rmh-eyebrow mt-6 text-[0.56rem]">{copy.eyebrow}</p>
          <h1 className="rmh-h2 mt-3 text-[1.6rem]">{copy.title}</h1>
          <p className="rmh-body mt-3 text-[0.88rem]">{copy.body}</p>

          <label
            htmlFor="admin-password"
            className="rmh-eyebrow mt-7 block text-[0.56rem]"
          >
            {copy.passwordLabel}
          </label>
          <Input
            id="admin-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError('');
            }}
            placeholder={copy.passwordPlaceholder}
            aria-invalid={Boolean(error)}
            data-testid="leads-login-password-input"
            className="mt-3 h-12 rounded-[12px] text-[0.92rem]"
            style={{
              backgroundColor: 'var(--rmh-surface-inset)',
              borderColor: error ? 'var(--rmh-mocha)' : 'var(--rmh-border)',
            }}
          />
          {error ? (
            <p
              className="mt-2.5 flex items-center gap-1.5 text-[0.78rem]"
              style={{ color: 'var(--rmh-mocha)' }}
              data-testid="leads-login-error"
            >
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            disabled={busy}
            data-testid="leads-login-submit-button"
            className="mt-7 h-12 w-full rounded-[12px] text-[0.86rem] font-medium"
          >
            {busy ? copy.signingIn : copy.submit}
          </Button>

          <div className="mt-7 flex items-center justify-between gap-3">
            <Link
              to="/"
              data-testid="leads-login-back-link"
              className="inline-flex items-center gap-1.5 text-[0.78rem] text-[color:var(--rmh-muted)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {AGENCY.website}
            </Link>
            <LanguageSwitcher />
          </div>
        </form>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------- primitives */

const StatCard = ({ label, value, testId, accent = false }) => (
  <div
    data-testid={testId}
    className="rounded-[14px] border p-5"
    style={{
      backgroundColor: accent ? 'var(--rmh-ink)' : 'var(--rmh-surface)',
      borderColor: accent ? 'var(--rmh-ink)' : 'var(--rmh-border)',
      boxShadow: 'var(--shadow-hairline)',
    }}
  >
    <p
      className="rmh-eyebrow text-[0.54rem]"
      style={accent ? { color: 'rgba(250,247,242,0.62)' } : undefined}
    >
      {label}
    </p>
    <p
      className="rmh-num mt-3 text-[1.9rem] leading-none"
      style={{ color: accent ? 'var(--rmh-canvas)' : 'var(--rmh-ink)' }}
    >
      {value}
    </p>
  </div>
);

const SourceBadge = ({ source, labels }) => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[0.68rem] font-medium uppercase tracking-[0.12em]"
    style={{
      backgroundColor: 'var(--rmh-surface-inset)',
      borderColor: 'var(--rmh-border)',
      color: 'var(--rmh-mocha)',
    }}
  >
    {source === 'romilda_ai' ? (
      <>
        <Sparkles className="h-3 w-3" />
        {labels.sourceAi}
      </>
    ) : (
      labels.sourceForm
    )}
  </span>
);

const StatusPill = ({ status, labels }) => {
  const tone = {
    new: { bg: 'var(--rmh-accent)', fg: '#141312' },
    read: { bg: 'var(--rmh-surface-inset)', fg: 'var(--rmh-muted)' },
    contacted: { bg: 'var(--rmh-mocha)', fg: 'var(--rmh-canvas)' },
    archived: { bg: 'var(--rmh-border)', fg: 'var(--rmh-muted)' },
  }[status] || { bg: 'var(--rmh-surface-inset)', fg: 'var(--rmh-muted)' };

  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-[3px] text-[0.66rem] font-medium uppercase tracking-[0.12em]"
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      {labels[status] || status}
    </span>
  );
};

/* -------------------------------------------------------------- detail UI */

const LeadDetailSheet = ({ leadId, open, onOpenChange, onStatusChange }) => {
  const { t } = useI18n();
  const copy = t.admin;
  const label = useCanonicalLabel();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !leadId) return;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    adminLeadDetail(leadId)
      .then((data) => {
        if (!cancelled) {
          setDetail(data);
          onStatusChange?.(leadId, data.lead.status);
        }
      })
      .catch(() => {
        if (!cancelled) toast.error(copy.loadError);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId]);

  const lead = detail?.lead;
  const messages = detail?.messages || [];

  const changeStatus = async (next) => {
    if (!lead) return;
    setSaving(true);
    try {
      const updated = await adminUpdateLead(lead.id, { status: next });
      setDetail((d) => ({ ...d, lead: updated }));
      onStatusChange?.(lead.id, updated.status);
      toast.success(copy.statusUpdated);
    } catch {
      toast.error(copy.statusFailed);
    } finally {
      setSaving(false);
    }
  };

  const copyEmail = async () => {
    if (!lead?.email) return;
    try {
      await navigator.clipboard.writeText(lead.email);
      toast.success(copy.detail.copied);
    } catch {
      /* clipboard blocked */
    }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString(t.intlLocale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="leads-detail-sheet"
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[540px]"
        style={{ backgroundColor: 'var(--rmh-canvas)' }}
      >
        <SheetHeader
          className="shrink-0 space-y-0 border-b px-6 py-5 text-left"
          style={{
            backgroundColor: 'var(--rmh-surface)',
            borderColor: 'var(--rmh-border)',
          }}
        >
          <SheetTitle className="font-serif text-[1.2rem] font-normal text-[color:var(--rmh-ink)]">
            {copy.detail.title}
          </SheetTitle>
          <SheetDescription className="text-[0.8rem] text-[color:var(--rmh-muted)]">
            {lead ? fmt(lead.created_at) : ''}
          </SheetDescription>
        </SheetHeader>

        <div className="rmh-scroll flex-1 overflow-y-auto px-6 py-6">
          {loading || !lead ? (
            <div className="flex flex-col gap-4" data-testid="leads-detail-loading">
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : (
            <>
              {/* identity */}
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-[1.4rem] leading-snug text-[color:var(--rmh-ink)]">
                    {lead.name || copy.lead.unnamed}
                  </h2>
                  {lead.brand ? (
                    <p className="mt-1 text-[0.86rem] text-[color:var(--rmh-muted)]">
                      {lead.brand}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <SourceBadge source={lead.source} labels={copy.lead} />
                  <StatusPill status={lead.status} labels={copy.status} />
                </div>
              </div>

              {/* contact actions */}
              <div className="mt-6 rounded-[14px] border p-5"
                style={{
                  backgroundColor: 'var(--rmh-surface)',
                  borderColor: 'var(--rmh-border)',
                }}
              >
                <p className="rmh-eyebrow text-[0.54rem]">{copy.detail.contact}</p>
                {lead.email ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <a
                      href={`mailto:${lead.email}`}
                      data-testid="leads-detail-email-link"
                      className="inline-flex items-center gap-2 text-[0.88rem] text-[color:var(--rmh-ink)] transition-colors duration-300 hover:text-[color:var(--rmh-mocha)]"
                    >
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      {lead.email}
                    </a>
                    <button
                      type="button"
                      onClick={copyEmail}
                      aria-label={copy.detail.copyEmail}
                      title={copy.detail.copyEmail}
                      data-testid="leads-detail-copy-email-button"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full border text-[color:var(--rmh-muted)] transition-colors duration-300 hover:text-[color:var(--rmh-ink)]"
                      style={{ borderColor: 'var(--rmh-border-strong)' }}
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <p className="mt-3 text-[0.86rem] text-[color:var(--rmh-muted-2)]">
                    {copy.detail.noEmail}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {lead.email ? (
                    <a
                      href={`mailto:${lead.email}`}
                      data-testid="leads-detail-reply-button"
                      className="inline-flex h-9 items-center gap-2 rounded-[10px] px-4 text-[0.78rem] font-medium transition-colors duration-300"
                      style={{
                        backgroundColor: 'var(--rmh-ink)',
                        color: 'var(--rmh-canvas)',
                      }}
                    >
                      <Mail className="h-3.5 w-3.5" />
                      {copy.detail.emailAction}
                    </a>
                  ) : null}
                  <a
                    href={whatsappLink(t.whatsappMessage)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="leads-detail-whatsapp-link"
                    className="inline-flex h-9 items-center gap-2 rounded-[10px] border px-4 text-[0.78rem] font-medium text-[color:var(--rmh-ink)] transition-colors duration-300 hover:border-[color:var(--rmh-ink)]"
                    style={{
                      backgroundColor: 'var(--rmh-surface)',
                      borderColor: 'var(--rmh-border-strong)',
                    }}
                  >
                    <WhatsAppGlyph className="h-3.5 w-3.5" />
                    {copy.detail.whatsappAction}
                  </a>
                </div>

                <div className="rmh-rule mt-5" />
                <div className="mt-4">
                  <p className="rmh-eyebrow text-[0.54rem]">
                    {copy.detail.statusLabel}
                  </p>
                  <Select
                    value={lead.status}
                    onValueChange={changeStatus}
                    disabled={saving}
                  >
                    <SelectTrigger
                      data-testid="leads-detail-status-select"
                      className="mt-3 h-10 rounded-[10px] text-[0.84rem]"
                      style={{
                        backgroundColor: 'var(--rmh-surface-inset)',
                        borderColor: 'var(--rmh-border)',
                      }}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={`leads-detail-status-option-${s}`}
                        >
                          {copy.status[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* facts */}
              {lead.service_interest ||
              lead.platform ||
              lead.budget ||
              lead.timeline ? (
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {lead.service_interest ? (
                    <div
                      className="rounded-[12px] border p-4"
                      style={{
                        backgroundColor: 'var(--rmh-surface-2)',
                        borderColor: 'var(--rmh-border)',
                      }}
                      data-testid="leads-detail-interest"
                    >
                      <p className="rmh-eyebrow text-[0.52rem]">
                        {copy.lead.interest}
                      </p>
                      <p className="mt-2 text-[0.85rem] text-[color:var(--rmh-ink)]">
                        {label(lead.service_interest)}
                      </p>
                    </div>
                  ) : null}
                  {lead.platform ? (
                    <div
                      className="rounded-[12px] border p-4"
                      style={{
                        backgroundColor: 'var(--rmh-surface-2)',
                        borderColor: 'var(--rmh-border)',
                      }}
                      data-testid="leads-detail-platform"
                    >
                      <p className="rmh-eyebrow text-[0.52rem]">
                        {copy.lead.platform}
                      </p>
                      <p className="mt-2 text-[0.85rem] text-[color:var(--rmh-ink)]">
                        {lead.platform}
                      </p>
                    </div>
                  ) : null}
                  {lead.budget ? (
                    <div
                      className="rounded-[12px] border p-4"
                      style={{
                        backgroundColor: 'var(--rmh-surface-2)',
                        borderColor: 'var(--rmh-border)',
                      }}
                      data-testid="leads-detail-budget"
                    >
                      <p className="rmh-eyebrow text-[0.52rem]">
                        {copy.lead.budget}
                      </p>
                      <p className="mt-2 text-[0.85rem] text-[color:var(--rmh-ink)]">
                        {label(lead.budget)}
                      </p>
                    </div>
                  ) : null}
                  {lead.timeline ? (
                    <div
                      className="rounded-[12px] border p-4"
                      style={{
                        backgroundColor: 'var(--rmh-surface-2)',
                        borderColor: 'var(--rmh-border)',
                      }}
                      data-testid="leads-detail-timeline"
                    >
                      <p className="rmh-eyebrow text-[0.52rem]">
                        {copy.lead.timeline}
                      </p>
                      <p className="mt-2 text-[0.85rem] text-[color:var(--rmh-ink)]">
                        {label(lead.timeline)}
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {lead.summary ? (
                <div
                  className="mt-5 rounded-[14px] border p-5"
                  style={{
                    backgroundColor: 'var(--rmh-canvas-2)',
                    borderColor: 'var(--rmh-border)',
                  }}
                  data-testid="leads-detail-summary"
                >
                  <p className="rmh-eyebrow flex items-center gap-2 text-[0.54rem]">
                    <Sparkles className="h-3 w-3" />
                    {copy.lead.summary}
                  </p>
                  <p className="rmh-body mt-3 text-[0.88rem]">{lead.summary}</p>
                </div>
              ) : null}

              {lead.challenge ? (
                <div className="mt-5" data-testid="leads-detail-challenge">
                  <p className="rmh-eyebrow text-[0.54rem]">{copy.lead.challenge}</p>
                  <p
                    className="mt-3 border-l-2 pl-4 text-[0.9rem] leading-relaxed text-[color:var(--rmh-ink-2)]"
                    style={{ borderColor: 'var(--rmh-accent)' }}
                  >
                    {lead.challenge}
                  </p>
                </div>
              ) : null}

              {lead.message ? (
                <div className="mt-5" data-testid="leads-detail-message">
                  <p className="rmh-eyebrow text-[0.54rem]">{copy.lead.message}</p>
                  <p className="rmh-body mt-3 text-[0.9rem]">{lead.message}</p>
                </div>
              ) : null}

              {/* transcript */}
              <div className="mt-8">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="rmh-eyebrow flex items-center gap-2 text-[0.54rem]">
                    <MessageSquare className="h-3 w-3" />
                    {copy.detail.conversation}
                  </p>
                  {messages.length ? (
                    <span className="rmh-num text-[0.68rem] text-[color:var(--rmh-muted-2)]">
                      {interpolate(copy.detail.messagesCount, {
                        count: messages.length,
                      })}
                    </span>
                  ) : null}
                </div>

                {messages.length === 0 ? (
                  <p
                    className="mt-4 rounded-[12px] border p-4 text-[0.84rem] text-[color:var(--rmh-muted)]"
                    style={{
                      backgroundColor: 'var(--rmh-surface-2)',
                      borderColor: 'var(--rmh-border)',
                    }}
                    data-testid="leads-detail-conversation-empty"
                  >
                    {copy.detail.conversationEmpty}
                  </p>
                ) : (
                  <div
                    className="mt-4 flex flex-col gap-3"
                    data-testid="leads-detail-conversation"
                  >
                    {messages.map((m, i) => (
                      <div
                        key={`${m.ts}-${i}`}
                        className={cn(
                          'flex',
                          m.role === 'user' ? 'justify-end' : 'justify-start',
                        )}
                        data-testid={`leads-detail-message-${i}`}
                      >
                        <div
                          className={cn(
                            'max-w-[88%] rounded-2xl px-4 py-3 text-[0.84rem] leading-relaxed',
                            m.role === 'user'
                              ? 'rounded-br-md'
                              : 'rounded-tl-md border',
                          )}
                          style={
                            m.role === 'user'
                              ? {
                                  backgroundColor: 'var(--rmh-ink)',
                                  color: 'var(--rmh-canvas)',
                                }
                              : {
                                  backgroundColor: 'var(--rmh-surface-2)',
                                  borderColor: 'var(--rmh-border)',
                                  color: 'var(--rmh-ink-2)',
                                }
                          }
                        >
                          <p
                            className="mb-1.5 text-[0.6rem] font-medium uppercase tracking-[0.14em]"
                            style={{
                              color:
                                m.role === 'user'
                                  ? 'rgba(250,247,242,0.6)'
                                  : 'var(--rmh-muted-2)',
                            }}
                          >
                            {m.role === 'user'
                              ? copy.detail.visitor
                              : copy.detail.assistant}
                          </p>
                          <p className="whitespace-pre-wrap">{m.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* ---------------------------------------------------------------- dashboard */

const Dashboard = ({ onSignOut }) => {
  const { t } = useI18n();
  const label = useCanonicalLabel();
  const copy = t.admin;

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const filters = useMemo(
    () => [
      { key: 'all', label: copy.filters.all },
      { key: 'new', label: copy.filters.new },
      { key: 'contact_form', label: copy.filters.form },
      { key: 'romilda_ai', label: copy.filters.ai },
    ],
    [copy],
  );

  const load = useCallback(
    async (opts = {}) => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        const activeFilter = opts.filter ?? filter;
        const activeQuery = opts.query ?? query;
        if (activeFilter === 'new') params.status = 'new';
        else if (activeFilter !== 'all') params.source = activeFilter;
        if (activeQuery.trim()) params.q = activeQuery.trim();

        const [rows, counters] = await Promise.all([listLeads(params), adminStats()]);
        setLeads(rows);
        setStats(counters);
      } catch (err) {
        if (err?.response?.status === 401) {
          onSignOut();
          return;
        }
        setError(copy.loadError);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, query, copy.loadError],
  );

  useEffect(() => {
    load({ filter, query });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadLeadsCsv();
    } catch {
      toast.error(copy.exportFailed);
    } finally {
      setExporting(false);
    }
  };

  const applyStatus = (id, nextStatus) => {
    setLeads((prev) =>
      prev.map((l) => (l.id === id ? { ...l, status: nextStatus } : l)),
    );
    adminStats().then(setStats).catch(() => {});
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(t.intlLocale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const isFiltered = filter !== 'all' || Boolean(query.trim());

  return (
    <div className="min-h-screen py-10 lg:py-14" style={{ backgroundColor: 'var(--rmh-canvas)' }}>
      <Container wide>
        {/* header */}
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span
                className="block h-px w-10"
                style={{ backgroundColor: 'var(--rmh-accent)' }}
                aria-hidden="true"
              />
              <span className="rmh-eyebrow">{copy.eyebrow}</span>
            </div>
            <h1 className="rmh-display mt-5 text-[2rem] sm:text-[2.5rem]">
              {copy.title}
            </h1>
            <p className="rmh-body mt-4 max-w-xl text-[0.9rem]">{copy.subtitle}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <LanguageSwitcher />
            <Button
              type="button"
              variant="outline"
              onClick={exportCsv}
              disabled={exporting}
              data-testid="leads-export-button"
              className="h-10 gap-2 rounded-[12px] text-[0.8rem]"
              style={{
                backgroundColor: 'var(--rmh-surface)',
                borderColor: 'var(--rmh-border-strong)',
              }}
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? copy.exporting : copy.exportCsv}
            </Button>
            <Button
              type="button"
              onClick={() => load()}
              data-testid="leads-refresh-button"
              className="h-10 gap-2 rounded-[12px] text-[0.8rem]"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
              {copy.refresh}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onSignOut}
              data-testid="leads-sign-out-button"
              className="h-10 gap-2 rounded-[12px] text-[0.8rem] text-[color:var(--rmh-muted)]"
            >
              <LogOut className="h-3.5 w-3.5" />
              {copy.signOut}
            </Button>
          </div>
        </div>

        {/* stats */}
        <div className="mt-10 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard
            label={copy.stats.total}
            value={stats ? stats.total : '—'}
            testId="leads-stat-total"
          />
          <StatCard
            label={copy.stats.unread}
            value={stats ? stats.unread : '—'}
            testId="leads-stat-unread"
            accent
          />
          <StatCard
            label={copy.stats.form}
            value={stats ? stats.contact_form : '—'}
            testId="leads-stat-form"
          />
          <StatCard
            label={copy.stats.ai}
            value={stats ? stats.romilda_ai : '—'}
            testId="leads-stat-ai"
          />
        </div>

        {/* controls */}
        <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2" role="group" aria-label={copy.filters.all}>
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                aria-pressed={filter === f.key}
                data-testid={`leads-filter-${f.key}-button`}
                className={cn(
                  'rounded-full border px-4 py-2 text-[0.78rem] font-medium',
                  'transition-[background-color,border-color,color] duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--rmh-accent)]',
                  filter === f.key
                    ? 'text-[color:var(--rmh-canvas)]'
                    : 'text-[color:var(--rmh-ink-2)] hover:border-[color:var(--rmh-ink)]',
                )}
                style={{
                  backgroundColor:
                    filter === f.key ? 'var(--rmh-ink)' : 'var(--rmh-surface)',
                  borderColor:
                    filter === f.key ? 'var(--rmh-ink)' : 'var(--rmh-border-strong)',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              load();
            }}
            className="flex items-center gap-2 rounded-[12px] border px-3"
            style={{
              backgroundColor: 'var(--rmh-surface)',
              borderColor: 'var(--rmh-border)',
            }}
          >
            <Search className="h-3.5 w-3.5 shrink-0 text-[color:var(--rmh-muted-2)]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={copy.searchPlaceholder}
              aria-label={copy.searchAria}
              data-testid="leads-search-input"
              className="h-10 w-full min-w-[180px] bg-transparent text-[0.84rem] text-[color:var(--rmh-ink)] outline-none placeholder:text-[color:var(--rmh-muted-2)] sm:w-64"
            />
            {query ? (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  load({ query: '' });
                }}
                aria-label={copy.detail.close}
                data-testid="leads-search-clear-button"
                className="shrink-0 text-[color:var(--rmh-muted-2)] hover:text-[color:var(--rmh-ink)]"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </form>
        </div>

        {/* list */}
        <div className="mt-7" data-testid="leads-table">
          {error ? (
            <div
              className="rounded-[14px] border p-5"
              style={{
                backgroundColor: 'var(--rmh-surface)',
                borderColor: 'var(--rmh-border-strong)',
              }}
              data-testid="leads-error"
            >
              <p
                className="flex items-center gap-2 text-[0.86rem]"
                style={{ color: 'var(--rmh-mocha)' }}
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </p>
              <Button
                type="button"
                onClick={() => load()}
                data-testid="leads-error-retry-button"
                className="mt-4 h-9 rounded-[10px] text-[0.8rem]"
              >
                {t.common.tryAgain}
              </Button>
            </div>
          ) : loading ? (
            <div className="flex flex-col gap-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-28 w-full rounded-[14px]" />
              ))}
            </div>
          ) : leads.length === 0 ? (
            <div
              className="rounded-[16px] border p-10 text-center"
              style={{
                backgroundColor: 'var(--rmh-surface)',
                borderColor: 'var(--rmh-border)',
              }}
              data-testid="leads-empty-state"
            >
              <span
                className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full border"
                style={{
                  backgroundColor: 'var(--rmh-surface-inset)',
                  borderColor: 'var(--rmh-border)',
                }}
              >
                <Inbox
                  className="h-5 w-5"
                  strokeWidth={1.5}
                  style={{ color: 'var(--rmh-mocha)' }}
                />
              </span>
              <p className="mt-6 font-serif text-[1.2rem] text-[color:var(--rmh-ink)]">
                {isFiltered ? copy.emptyFiltered.title : copy.empty.title}
              </p>
              <p className="rmh-body mx-auto mt-3 max-w-md text-[0.88rem]">
                {isFiltered ? copy.emptyFiltered.body : copy.empty.body}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {leads.map((lead) => (
                <button
                  key={lead.id}
                  type="button"
                  onClick={() => setActiveId(lead.id)}
                  data-testid={`leads-row-${lead.id}`}
                  className={cn(
                    'w-full rounded-[14px] border p-5 text-left sm:p-6',
                    'transition-[border-color,box-shadow,transform] duration-300',
                    'hover:-translate-y-[1px] hover:border-[color:var(--rmh-border-strong)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--rmh-accent)]',
                  )}
                  style={{
                    backgroundColor: 'var(--rmh-surface)',
                    borderColor:
                      lead.status === 'new'
                        ? 'var(--rmh-accent)'
                        : 'var(--rmh-border)',
                    boxShadow: 'var(--shadow-hairline)',
                  }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2 font-serif text-[1.08rem] text-[color:var(--rmh-ink)]">
                        {lead.name || copy.lead.unnamed}
                        {lead.brand ? (
                          <span className="text-[color:var(--rmh-muted)]">
                            · {lead.brand}
                          </span>
                        ) : null}
                        {lead.status === 'new' ? (
                          <Badge
                            className="h-5 border-none px-2 text-[0.6rem] uppercase tracking-[0.14em]"
                            style={{
                              backgroundColor: 'var(--rmh-accent)',
                              color: '#141312',
                            }}
                          >
                            {copy.lead.newBadge}
                          </Badge>
                        ) : null}
                      </p>
                      {lead.email ? (
                        <span className="mt-1.5 inline-flex items-center gap-1.5 text-[0.82rem] text-[color:var(--rmh-muted)]">
                          <Mail className="h-3 w-3 shrink-0" />
                          {lead.email}
                        </span>
                      ) : null}
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <SourceBadge source={lead.source} labels={copy.lead} />
                      <span className="rmh-num text-[0.7rem] text-[color:var(--rmh-muted-2)]">
                        {fmt(lead.created_at)}
                      </span>
                    </div>
                  </div>

                  {lead.service_interest ? (
                    <p className="mt-4 text-[0.82rem] text-[color:var(--rmh-ink-2)]">
                      <span className="rmh-eyebrow text-[0.52rem]">
                        {copy.lead.interest}
                      </span>
                      <span className="ml-2">{label(lead.service_interest)}</span>
                    </p>
                  ) : null}

                  {lead.summary ? (
                    <p
                      className="mt-3 line-clamp-2 border-l-2 pl-4 text-[0.84rem] italic text-[color:var(--rmh-muted)]"
                      style={{ borderColor: 'var(--rmh-accent)' }}
                    >
                      {lead.summary}
                    </p>
                  ) : lead.challenge ? (
                    <p className="rmh-body mt-3 line-clamp-2 text-[0.86rem]">
                      {lead.challenge}
                    </p>
                  ) : null}

                  <span className="mt-4 inline-flex items-center gap-2 text-[0.72rem] font-medium uppercase tracking-[0.14em] text-[color:var(--rmh-ink)]">
                    {lead.source === 'romilda_ai' ? (
                      <MessageSquare className="h-3 w-3" />
                    ) : (
                      <Check className="h-3 w-3" />
                    )}
                    {copy.lead.viewDetails}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </Container>

      <LeadDetailSheet
        leadId={activeId}
        open={Boolean(activeId)}
        onOpenChange={(next) => {
          if (!next) setActiveId(null);
        }}
        onStatusChange={applyStatus}
      />
    </div>
  );
};

/* --------------------------------------------------------------------- page */

export default function Leads() {
  const [authed, setAuthed] = useState(() => Boolean(getAdminToken()));

  const signOut = useCallback(() => {
    clearAdminToken();
    setAuthed(false);
  }, []);

  if (!authed) {
    return <LoginGate onSuccess={() => setAuthed(true)} />;
  }
  return <Dashboard onSignOut={signOut} />;
}
